/**
 * useWebSocket Hook
 * 
 * React hook for using WebSocket real-time updates.
 * Automatically connects on mount and disconnects on unmount.
 * 
 * Usage:
 * ```
 * const { isConnected, bookingUpdates } = useWebSocket({
 *   phone: user.phone,
 *   propertyId: selectedProperty.id,
 *   onBookingUpdate: (payload) => console.log('Booking updated:', payload),
 * });
 * ```
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import websocketService, {
  connectWebSocket,
  disconnectWebSocket,
  subscribeToUser,
  subscribeToProperty,
  onWebSocketEvent,
  getWebSocketStatus,
} from '../services/websocket';

/**
 * Hook for WebSocket real-time updates
 */
export const useWebSocket = (options = {}) => {
  const {
    phone,
    propertyId,
    onBookingUpdate,
    onPropertyUpdate,
    onLeadUpdate,
    onCrmUpdate,
    onSyncComplete,
    autoConnect = true,
  } = options;

  const [isConnected, setIsConnected] = useState(false);
  const [lastUpdate, setLastUpdate] = useState(null);
  const [bookingUpdates, setBookingUpdates] = useState([]);
  const unsubscribersRef = useRef([]);

  // Connect on mount
  useEffect(() => {
    if (autoConnect) {
      connectWebSocket();
    }

    // Cleanup on unmount
    return () => {
      unsubscribersRef.current.forEach(unsub => unsub());
      unsubscribersRef.current = [];
    };
  }, [autoConnect]);

  // Subscribe to user-specific updates
  useEffect(() => {
    if (phone) {
      subscribeToUser(phone);
    }
  }, [phone]);

  // Subscribe to property-specific updates
  useEffect(() => {
    if (propertyId) {
      subscribeToProperty(propertyId);
    }
  }, [propertyId]);

  // Setup event listeners
  useEffect(() => {
    const unsubs = [];

    // Connection status
    unsubs.push(
      onWebSocketEvent('connection', (data) => {
        setIsConnected(data.status === 'connected');
      })
    );

    // Booking updates
    unsubs.push(
      onWebSocketEvent('booking_update', (payload) => {
        setLastUpdate({ type: 'booking', payload, timestamp: new Date() });
        setBookingUpdates(prev => [payload, ...prev].slice(0, 50)); // Keep last 50
        onBookingUpdate?.(payload);
      })
    );

    // Property updates
    unsubs.push(
      onWebSocketEvent('property_update', (payload) => {
        setLastUpdate({ type: 'property', payload, timestamp: new Date() });
        onPropertyUpdate?.(payload);
      })
    );

    // Lead updates
    unsubs.push(
      onWebSocketEvent('lead_update', (payload) => {
        setLastUpdate({ type: 'lead', payload, timestamp: new Date() });
        onLeadUpdate?.(payload);
      })
    );

    // CRM updates
    unsubs.push(
      onWebSocketEvent('crm_update', (payload) => {
        setLastUpdate({ type: 'crm', payload, timestamp: new Date() });
        onCrmUpdate?.(payload);
      })
    );

    // Sync complete
    unsubs.push(
      onWebSocketEvent('sync_complete', (payload) => {
        setLastUpdate({ type: 'sync', payload, timestamp: new Date() });
        onSyncComplete?.(payload);
      })
    );

    unsubscribersRef.current = unsubs;

    return () => {
      unsubs.forEach(unsub => unsub());
    };
  }, [onBookingUpdate, onPropertyUpdate, onLeadUpdate, onCrmUpdate, onSyncComplete]);

  // Manual connect
  const connect = useCallback(() => {
    connectWebSocket();
  }, []);

  // Manual disconnect
  const disconnect = useCallback(() => {
    disconnectWebSocket();
    setIsConnected(false);
  }, []);

  // Get status
  const getStatus = useCallback(() => {
    return getWebSocketStatus();
  }, []);

  return {
    isConnected,
    lastUpdate,
    bookingUpdates,
    connect,
    disconnect,
    getStatus,
  };
};

/**
 * Hook for booking-specific real-time updates
 */
export const useBookingUpdates = (propertyId, onUpdate) => {
  const [updates, setUpdates] = useState([]);

  useEffect(() => {
    if (propertyId) {
      subscribeToProperty(propertyId);
    }

    const unsub = onWebSocketEvent('booking_update', (payload) => {
      // Only process if matches our property
      if (!propertyId || payload.data?.property_id === propertyId) {
        setUpdates(prev => [payload, ...prev].slice(0, 20));
        onUpdate?.(payload);
      }
    });

    return unsub;
  }, [propertyId, onUpdate]);

  return updates;
};

/**
 * Hook for sync status
 */
export const useSyncStatus = () => {
  const [lastSync, setLastSync] = useState(null);

  useEffect(() => {
    const unsub = onWebSocketEvent('sync_complete', (payload) => {
      setLastSync({
        type: payload.sync_type,
        stats: payload.stats,
        timestamp: new Date(payload.timestamp),
      });
    });

    return unsub;
  }, []);

  return lastSync;
};

export default useWebSocket;
